import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import './LegalPage.css';

function Terms() {
  useEffect(() => {
    document.title = 'Terms & Conditions - My Fair Share UK Tax Calculator';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms and conditions for using My Fair Share UK tax calculator. Read our terms of service and usage guidelines.');
    }
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Calculator</Link>
        
        <h1>Terms &amp; Conditions</h1>
        <p className="last-updated">Last updated: November 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to My Fair Share. These terms and conditions govern your use of our website 
            and tax calculation tools. By using this website, you accept these terms in full. 
            If you disagree with any part of these terms, please do not use our website.
          </p>
        </section>

        <section>
          <h2>2. Service Description</h2>
          <p>
            My Fair Share provides a free online tool for estimating UK income tax and National 
            Insurance contributions. The calculations are based on publicly available HMRC 
            tax rates and thresholds for the 2025-2026 tax year.
          </p>
          <p>
            The tool also provides hypothetical "what-if" scenarios that illustrate how 
            alternative tax policies could affect individual tax liability. These simulations 
            are for educational and informational purposes only.
          </p>
        </section>

        <section>
          <h2>3. No Professional Advice</h2>
          <p>
            <strong>Important:</strong> The information and calculations provided by My Fair Share 
            are for general information and educational purposes only. They do not constitute 
            financial, tax, legal, or other professional advice.
          </p>
          <p>
            The calculations provided:
          </p>
          <ul>
            <li>Are estimates only and may not reflect your actual tax liability</li>
            <li>Do not account for all possible tax reliefs, allowances, or circumstances</li>
            <li>May not be suitable for complex tax situations</li>
            <li>Should not be relied upon for making financial decisions</li>
          </ul>
          <p>
            For accurate tax calculations and advice, please consult HMRC directly or 
            seek advice from a qualified tax professional or accountant.
          </p>
        </section>

        <section>
          <h2>4. Accuracy of Information</h2>
          <p>
            While we strive to ensure the accuracy of our calculations and information, 
            we make no warranties or representations about:
          </p>
          <ul>
            <li>The accuracy, completeness, or reliability of any calculations</li>
            <li>The suitability of the information for your particular circumstances</li>
            <li>The availability or uninterrupted operation of our services</li>
          </ul>
          <p>
            Tax rates and thresholds are subject to change by the government. We endeavour 
            to update our calculations promptly, but there may be delays.
          </p>
        </section>

        <section>
          <h2>5. Limitation of Liability</h2>
          <p>
            To the fullest extent permitted by law, My Fair Share and its creators shall not be 
            liable for any direct, indirect, incidental, consequential, or punitive damages 
            arising from:
          </p>
          <ul>
            <li>Your use of, or inability to use, this website or tool</li>
            <li>Any errors or inaccuracies in the calculations provided</li>
            <li>Any decisions made based on information from this website</li>
            <li>Any loss of data or business interruption</li>
          </ul>
          <p>
            Nothing in these terms excludes or limits liability for death or personal injury 
            caused by negligence, fraud or fraudulent misrepresentation, or any other 
            liability that cannot be excluded or limited under English law.
          </p>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            The content, design, and code of My Fair Share are protected by copyright and other 
            intellectual property rights. You may use the website for personal, 
            non-commercial purposes only.
          </p>
          <p>
            You may not reproduce, distribute, modify, or create derivative works from any 
            part of this website without our prior written consent.
          </p>
        </section>

        <section>
          <h2>7. User Responsibilities</h2>
          <p>When using My Fair Share, you agree to:</p>
          <ul>
            <li>Use the website only for lawful purposes</li>
            <li>Not attempt to gain unauthorised access to our systems</li>
            <li>Not use automated tools to access the website in a way that could damage or impair it</li>
            <li>Not use the service in any way that violates applicable laws or regulations</li>
          </ul>
        </section>

        <section>
          <h2>8. Privacy</h2>
          <p>
            Your privacy is important to us. Please review our{' '}
            <Link to="/privacy">Privacy Policy</Link> to understand how we handle your data. 
            All calculations are performed locally in your browser, and we do not store or 
            transmit your salary information.
          </p>
        </section>

        <section>
          <h2>9. External Links</h2>
          <p>
            This website may contain links to external websites. We have no control over the 
            content of these websites and accept no responsibility for them or for any loss 
            or damage arising from your use of them.
          </p>
        </section>

        <section>
          <h2>10. Changes to Terms</h2>
          <p>
            We may update these terms from time to time. Any changes will be posted on this 
            page with an updated revision date. Continued use of the website after changes 
            constitutes acceptance of the new terms.
          </p>
        </section>

        <section>
          <h2>11. Governing Law</h2>
          <p>
            These terms and conditions are governed by and construed in accordance with 
            the laws of England and Wales. Any disputes relating to these terms shall be 
            subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section>
          <h2>12. Contact</h2>
          <p>
            If you have any questions about these terms, please{' '}
            <Link to="/contact">contact us</Link>.
          </p>
        </section>

        <LegalNav excludeLinks={['/terms']} />
      </div>
    </div>
  );
}

export default Terms;
